/**
 * Whether the click will leave the current document instead of being handled in-page.
 * Modifier and non-primary clicks open a new tab/window or a context menu, so the
 * current document stays put.
 *
 * @param {MouseEvent} nativeEvent - Native click event.
 * @returns {boolean}
 */
const isPlainPrimaryClick = (nativeEvent) =>
  !nativeEvent.button && !nativeEvent.metaKey && !nativeEvent.ctrlKey && !nativeEvent.shiftKey && !nativeEvent.altKey;

/**
 * Whether the anchor points at a destination that unloads the current document.
 * Hash-only, empty and `javascript:` hrefs never do.
 *
 * @param {HTMLElement|null|undefined} element - Element the handler is bound to.
 * @returns {boolean}
 */
const hasNavigableHref = (element) => {
  const href = element?.getAttribute?.("href")?.trim();
  if (!href || href.startsWith("#")) {
    return false;
  }
  return !href.toLowerCase().startsWith("javascript:");
};

/**
 * Whether the anchor renders into another browsing context (new tab/window/frame).
 *
 * @param {HTMLElement|null|undefined} element - Element the handler is bound to.
 * @returns {boolean}
 */
const opensInAnotherTarget = (element) => {
  const target = element?.getAttribute?.("target");
  return Boolean(target) && target !== "_self";
};

/**
 * Runs `onNavigate` only for clicks that actually unload the current document, so
 * navigation-only side effects (skeleton overlays, loaders) never get stuck on a
 * click that goes nowhere.
 *
 * The decision is deferred by a microtask because carousel libraries cancel a
 * drag-click on an ancestor — react-slick calls `preventDefault()` on `.slick-track`,
 * which runs after the anchor's own handler. A microtask lands once the click has
 * finished propagating, so `defaultPrevented` is final by then.
 *
 * @param {React.MouseEvent} event - Click event from the anchor.
 * @param {Function} onNavigate - Called when the click really navigates.
 * @returns {void}
 */
export const runOnNavigationIntent = (event, onNavigate) => {
  if (typeof onNavigate !== "function") {
    return;
  }

  const nativeEvent = event?.nativeEvent ?? event;
  const element = event?.currentTarget;

  if (!nativeEvent) {
    onNavigate();
    return;
  }

  if (
    nativeEvent.defaultPrevented ||
    !isPlainPrimaryClick(nativeEvent) ||
    !hasNavigableHref(element) ||
    opensInAnotherTarget(element)
  ) {
    return;
  }

  Promise.resolve().then(() => {
    if (!nativeEvent.defaultPrevented) {
      onNavigate();
    }
  });
};
